import {
  IInvoiceAdjustmentInvoiceResult,
  IInvoiceAdjustmentRequest,
  InvoiceAdjustmentRequestStatus,
  InvoiceAdjustmentRequestType,
} from "@/api_framework/api_modals/payment";
import { format } from "date-fns";

export const INVOICE_ADJUSTMENT_MAX_ROWS: Record<InvoiceAdjustmentRequestType, number> = {
  FORCE_DISCOUNT: 200,
  BAD_DEBT: 75,
};
export const INVOICE_ID_COLUMN = "invoice_id";
export const SAMPLE_FILE_URL = "/invoice_adjustment_sample.csv";

const ALLOWED_EXTENSIONS = ["csv", "xlsx", "xls"];
const MAX_FILE_BYTES = 5 * 1024 * 1024;

export type IParsedInvoiceFile = { invoiceIds: string[]; error: string | null };

const normaliseHeader = (value: unknown) => String(value ?? "").trim().toLowerCase().replace(/\s+/g, "_");

export const parseInvoiceIdsFile = async (file: File, maxRows: number): Promise<IParsedInvoiceFile> => {
  const extension = file.name.split(".").pop()?.toLowerCase() ?? "";
  if (!ALLOWED_EXTENSIONS.includes(extension)) {
    return { invoiceIds: [], error: "Upload a .csv, .xlsx or .xls file" };
  }
  if (file.size > MAX_FILE_BYTES) {
    return { invoiceIds: [], error: "File is larger than 5 MB" };
  }

  let rows: unknown[][];
  try {
    const XLSX = await import("xlsx");
    // raw for CSV: keep invoice IDs as typed instead of guessing dates / numbers
    const workbook = XLSX.read(await file.arrayBuffer(), { type: "array", raw: extension === "csv" });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    if (!sheet) {
      return { invoiceIds: [], error: "The file has no sheets" };
    }
    rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, raw: false, defval: "", blankrows: false });
  } catch (e) {
    return { invoiceIds: [], error: "Could not read the file. Check that it is a valid CSV or Excel file" };
  }

  const columnIndex = (rows[0] ?? []).findIndex((cell) => normaliseHeader(cell) === INVOICE_ID_COLUMN);
  if (columnIndex === -1) {
    return { invoiceIds: [], error: `The first row needs an "${INVOICE_ID_COLUMN}" column` };
  }

  const invoiceIds = rows
    .slice(1)
    .map((row) => String(row?.[columnIndex] ?? "").trim())
    .filter((value) => value && !/^example\b/i.test(value));
  if (invoiceIds.length === 0) {
    return { invoiceIds: [], error: `No invoice IDs found under the "${INVOICE_ID_COLUMN}" column` };
  }
  if (invoiceIds.length > maxRows) {
    return {
      invoiceIds: [],
      error: `Maximum ${maxRows} invoice IDs per upload, this file has ${invoiceIds.length}`,
    };
  }
  return { invoiceIds, error: null };
};

export const REQUEST_TYPE_META: Record<
  InvoiceAdjustmentRequestType,
  { title: string; description: string; submitLabel: string }
> = {
  FORCE_DISCOUNT: {
    title: "Force Discount",
    description: "Write the unpaid due of invoices down as a discount. Runs as soon as it is uploaded.",
    submitLabel: "Apply Force Discount",
  },
  BAD_DEBT: {
    title: "Bad Debt",
    description: "Knock off unpaid dues as bad debt. Runs as soon as it is uploaded.",
    submitLabel: "Run Bad Debt",
  },
};

export const STATUS_META: Record<
  InvoiceAdjustmentRequestStatus,
  { label: string; colorClassName: string; fillClassName: string }
> = {
  PENDING_APPROVAL: { label: "Pending Approval", colorClassName: "bg-yellow-50 text-yellow-700", fillClassName: "fill-yellow-500" },
  APPROVED: { label: "Queued", colorClassName: "bg-blue-50 text-blue-700", fillClassName: "fill-blue-500" },
  PROCESSING: { label: "Processing", colorClassName: "bg-blue-50 text-blue-700", fillClassName: "fill-blue-500" },
  COMPLETED: { label: "Completed", colorClassName: "bg-green-50 text-green-700", fillClassName: "fill-green-500" },
  PARTIALLY_COMPLETED: { label: "Partially Completed", colorClassName: "bg-orange-50 text-orange-700", fillClassName: "fill-orange-500" },
  REJECTED: { label: "Rejected", colorClassName: "bg-red-50 text-red-700", fillClassName: "fill-red-500" },
  FAILED: { label: "Failed", colorClassName: "bg-red-50 text-red-700", fillClassName: "fill-red-500" },
};

export const STATUS_FILTERS: Record<InvoiceAdjustmentRequestType, InvoiceAdjustmentRequestStatus[]> = {
  FORCE_DISCOUNT: ["APPROVED", "PROCESSING", "COMPLETED", "PARTIALLY_COMPLETED", "FAILED"],
  BAD_DEBT: ["APPROVED", "PROCESSING", "COMPLETED", "PARTIALLY_COMPLETED", "FAILED"],
};

export const IN_FLIGHT_STATUSES: InvoiceAdjustmentRequestStatus[] = ["APPROVED", "PROCESSING"];

export const FINISHED_STATUSES: InvoiceAdjustmentRequestStatus[] = ["COMPLETED", "PARTIALLY_COMPLETED", "FAILED"];

const csvCell = (value: unknown) => {
  let text = String(value ?? "");
  if (/^[=+\-@]/.test(text)) text = `'${text}`;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const downloadRunResultCsv = (request: IInvoiceAdjustmentRequest) => {
  const invoiceNumberById = new Map(request.invoice_data.map((row) => [row.invoice_id, row.invoice_number]));
  const amountClosedById = new Map(request.invoice_data.map((row) => [row.invoice_id, row.amount_closed]));
  const results = (request.summary_dump ?? []).filter(
    (entry): entry is IInvoiceAdjustmentInvoiceResult => typeof entry === "object" && !!entry?.invoice_id,
  );
  const lines = [
    ["invoice_id", "invoice_number", "result", "amount_closed", "message"].join(","),
    ...results.map((result) =>
      [
        result.invoice_id,
        result.invoice_number ?? invoiceNumberById.get(result.invoice_id) ?? "",
        result.status,
        amountClosedById.get(result.invoice_id) ?? 0,
        result.message,
      ]
        .map(csvCell)
        .join(","),
    ),
  ];

  const blob = new Blob(["\uFEFF" + lines.join("\n") + "\n"], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${request.request_type.toLowerCase()}_result_${request.request_id.slice(0, 8)}_${format(
    new Date(),
    "yyyyMMdd_HHmm",
  )}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

